<template>
  <section class="location-box">
    <h3
      class="location-box__title wb-type-button"
      :style="hiddenTitle"
      :class="rangeControlActiveTitleClass"
    >
      <wb-icon name="bds/location/24"></wb-icon>
      {{ $t('search.location.title') }}
    </h3>
    <div class="location-box__place" :class="rangeControlActiveClass('left')">
      <wb-input-control :theme="theme" :class="locationInputClass">
        <wb-input @input="getSuggestions">
          <label>{{ $t('search.location.place') }}</label>
          <input
            :placeholder="$t('search.location.place')"
            v-model="initialLocationText"
            @focusout="onBlurInput"
            @keypress.enter="locationInputEnterPress"
            ref="locationInput"
            required
          />
          <wb-input-action>
            <wb-icon
              class="location-box__input-icon wb-round-button__icon"
              v-if="!freeSearchReadyMode"
              name="bds/location/24"
            />
            <button
              v-else
              class="wb-round-button wb-round-button--small"
              @click="selectInitialLocation(suggestion)"
              type="button"
            >
              <wb-icon class="wb-round-button__icon" name="bds/search/24" />
            </button>
          </wb-input-action>
        </wb-input>
        <wb-control-error
          v-if="locationInputErrorHandling"
          class="location-box__place-error"
          >{{ $t('header.location.search.error') }}</wb-control-error
        >
      </wb-input-control>
      <div
        class="wb-list-group"
        v-if="showSuggestions"
        :class="listGroupThemeClass"
      >
        <!-- TODO: Geolocation -->
        <!-- <button
          type="button"
          class="location-box__suggestion-list-item wb-list-group__item wb-list-group__item-selected"
        >
          <wb-icon name="locate-me"></wb-icon>
          {{ $t('search.location.place.suggestion') }}
        </button> -->
        <button
          type="button"
          @click="selectInitialLocation()"
          class="location-box__suggestion-list-item wb-list-group__item"
        >
          {{ initialLocationText }}
        </button>
        <button
          type="button"
          class="location-box__suggestion-list-item wb-list-group__item"
          :class="buttonThemeClass"
          v-for="suggestion in addressSuggestion"
          :key="suggestion.locationId"
          @click="selectInitialLocation(suggestion)"
        >
          {{ suggestion.label }}
        </button>
      </div>
    </div>
    <div
      class="location-box__radius"
      :class="rangeControlActiveClass('right')"
      v-if="dealerRadius"
    >
      <wb-select-control v-if="!rangeControlActive">
        <wb-select>
          <label>{{ $t('search.location.radius') }}</label>
          <select v-model="locationRadius" @change="selectLocationRadius">
            <option
              v-for="radius in dealerRadius"
              :key="radius + dealerRadiusUnit"
              :value="radius + dealerRadiusUnit"
              :selected="radius + dealerRadiusUnit === locationRadius"
            >
              {{ radius }} {{ dealerRadiusUnit }}
            </option>
          </select>
        </wb-select>
      </wb-select-control>
      <wb-range-control v-else theme="dark">
        <label class="location-box__radius-selected-label"
          >{{ $t('search.location.radius') }}:
          <span class="location-box__radius-selected-span"
            >{{ rangeControlRadius }} km</span
          ></label
        >
        <input type="range" :value="rangeControlRadius" step="15" />
      </wb-range-control>
    </div>

    <TestDriveFromHomeInformation
      :shouldHide="hideTitle"
      class="location-box__tdfh_container"
    />
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { useStore } from '../../composables/store.composable';
import { useUtils } from '../../composables/utils.composable';
import useRequestSelectionBoxes from '../../composables/request-selection-boxes.composable';
import { useMediaQuery } from '../../composables/media-query.composable';
import { LocationSuggestion } from '../../graphql/schema';

import UtilsService from '../../services/utils.service';

import { HiddenTitleStyles } from '../../models/entities/location-box.model';

import { TestDriveFromHomeInformation } from '../test-drive-from-home-information';

export default defineComponent({
  name: 'otb-location-box',
  components: {
    TestDriveFromHomeInformation,
  },
  props: {
    hideTitle: Boolean,
    initialTerm: String,
    validation: Boolean,
    rangeControlActive: Boolean,
    theme: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const { dispatch, state, commit } = useStore();
    const { hiddenTitleStyles } = useUtils();
    const { resetRequestSelectionBoxes } = useRequestSelectionBoxes();
    const shouldShowSuggestions = ref(false);
    const initialLocationText = ref(
      state.otbcFlow.initialLocation?.label ||
        state.otbcFlow.locationQueryFreeText ||
        state.otbcFlow.requestLocationQueryFreeText
    );
    const { browserDetect } = useMediaQuery();
    const textSuggestion = ref(props.initialTerm ? props.initialTerm : '');
    const locationRadius = ref('');
    const locationInputFocused = ref(false);
    const rangeControlRadius = ref(75);
    const locationInput = ref(null);

    const isScheduleRoute = computed((): boolean => {
      return props.validation;
    });

    const locationInputErrorHandling = computed((): boolean => {
      return props.validation && !initialLocationText.value;
    });

    const shouldShowRadiusGenericOption = computed((): any => {
      return !state.otbcFlow.locationRadius;
    });

    const addressSuggestion = computed((): LocationSuggestion[] => {
      return state.otbcFlow.addressSuggestion;
    });

    const dealerRadius = computed((): any => {
      return state.configuration.dealerRadiusArray;
    });

    const dealerRadiusUnit = computed((): any => {
      return state.configuration.dealerRadius.unit;
    });

    const hiddenTitle = computed((): HiddenTitleStyles => {
      return hiddenTitleStyles(props.hideTitle);
    });

    const showSuggestions = computed(
      (): boolean =>
        locationInputFocused.value &&
        shouldShowSuggestions.value &&
        initialLocationText.value.length
    );

    const freeSearchReadyMode = computed((): boolean => {
      return isScheduleRoute.value;
    });

    // TODO investigate further why on safari the getSuggestions method is being called more than once
    // this causes the addressSuggestion to be populated with the picked location, necessitating this check
    const debouncePeriod = browserDetect() === 'safari' ? 200 : 500;

    const getSuggestions = UtilsService.debounce((): void => {
      if (initialLocationText.value && initialLocationText.value.length >= 1) {
        locationInputFocused.value = true;
        commit('locationQueryFreeText', initialLocationText.value);
      }
      if (initialLocationText.value.length === 0) {
        commit('locationQueryFreeText', '');
      }
      if (initialLocationText.value && initialLocationText.value.length >= 2) {
        locationInputFocused.value = true;
        dispatch('getAddressSuggestion', {
          address: initialLocationText.value,
        });
      }
      shouldShowSuggestions.value = true;
    }, debouncePeriod);

    const selectInitialLocation = (suggestion?: LocationSuggestion): void => {
      if (suggestion) {
        commit('locationQueryFreeText', '');
      } else {
        commit('initialLocation', {
          label: initialLocationText.value,
          locationId: '',
          postalCode: '',
        });
        if (initialLocationText.value) {
          commit('locationQueryFreeText', initialLocationText.value);
        }
      }

      if (isScheduleRoute.value) {
        commit('bookingSearchFinished', false);
        dispatch('selectInitialLocation', { suggestion });
        dispatch('getTestDriveBookingVehicleSearchWithinRadius');
        return;
      }
      dispatch('selectInitialLocation', { suggestion });
      initialLocationText.value =
        suggestion && suggestion.label
          ? suggestion.label
          : state.otbcFlow.locationQueryFreeText;
      if (
        state.otbcFlow.bookingSearchFinished &&
        state.otbcFlow.locationRadius &&
        state.otbcFlow.initialLocation.locationId
      ) {
        commit('selectedDealerIds', []);
        resetRequestSelectionBoxes();
        dispatch('getTestDriveBookingVehicleSearchWithinRadius');
        commit('bookingSearchFinished', false);
        locationInputFocused.value = false;
      }
    };

    const selectLocationRadius = async (): Promise<void> => {
      if (locationRadius.value) {
        dispatch('selectLocationRadius', {
          locationRadius: locationRadius.value,
        });
        // TODO: To fix CDOTDB-900 this cannot be removed, or the call will be made on the define-location
        // step on radius change and it shouldn't. Eventually instead of this if condition it could
        // look at the router path ex.: if != '/define-location' do the dispatch
        if (
          state.otbcFlow.bookingSearchFinished &&
          (state.otbcFlow.initialLocation.locationId ||
            state.otbcFlow.locationQueryFreeText)
        ) {
          dispatch('getTestDriveBookingVehicleSearchWithinRadius');
        }
        // TODO: To fix CDOTDB-900 this cannot be moved to the top of the function or the call will not
        // be fired on results page.
        commit('bookingSearchFinished', false);
        commit('requestLocation', {
          label: state.otbcFlow.initialLocation?.label,
          locationId: state.otbcFlow.initialLocation?.locationId,
          postalCode: state.otbcFlow.initialLocation?.postalCode,
        });
      }
      if (
        state.otbcFlow.bookingSearchFinished &&
        locationRadius.value &&
        state.otbcFlow.initialLocation.locationId
      ) {
        commit('selectedDealerIds', []);
        resetRequestSelectionBoxes();
        dispatch('getTestDriveBookingVehicleSearchWithinRadius');
        commit('bookingSearchFinished', false);
      }
    };

    const onBlurInput = (e: FocusEvent): void => {
      //workaround to handle behavior in IOS
      setTimeout(() => {
        handleOutsideInputClick(e);
      }, 250);
    };

    const handleOutsideInputClick = (e: FocusEvent): void => {
      shouldShowSuggestions.value = false;
      const focusedElement = e.relatedTarget as HTMLElement;
      if (
        !focusedElement ||
        !focusedElement.classList.contains('location-box__suggestion-list-item')
      ) {
        locationInputFocused.value = false;
      }
    };

    const rangeControlActiveClass = (direction): string => {
      return props.rangeControlActive ? `location-box--${direction}` : '';
    };

    const rangeControlActiveTitleClass = computed((): string => {
      return props.rangeControlActive ? `location-box__title--dark-mode` : '';
    });

    const locationBoxInputIconClass = computed((): string => {
      return isScheduleRoute.value ? 'bds/search/24' : 'bds/location/24';
    });

    const locationInputClass = computed((): string => {
      return isScheduleRoute.value ? `location-box--input-schedule` : '';
    });
    const buttonThemeClass = computed((): string => {
      return props.theme === 'dark'
        ? 'wb-button--theme-dark wb-button--secondary'
        : '';
    });

    const listGroupThemeClass = computed((): string => {
      return props.theme === 'dark' ? 'wb-list-group--dark' : '';
    });

    const onEnterPress = (event) => {
      if (event.key === 'Enter') {
        onBlurInput(event);
        locationInputFocused.value = false;
        shouldShowSuggestions.value = false;
      }
    };
    const locationInputEnterPress = () => {
      selectInitialLocation();
      if (locationInput.value) {
        locationInput.value.blur();
      }
    };
    onMounted(() => {
      window.addEventListener('keyup', onEnterPress);
      if (state.otbcFlow.initialLocation?.label) {
        initialLocationText.value = state.otbcFlow.initialLocation?.label;
      }

      watch(
        dealerRadius,
        () => {
          if (state.otbcFlow.locationRadius) {
            locationRadius.value = state.otbcFlow.locationRadius;
          }
        },
        { immediate: true }
      );
    });

    watch(
      () =>
        state.otbcFlow.initialLocation?.label ||
        state.otbcFlow.locationQueryFreeText,
      (newLocation) => {
        initialLocationText.value = newLocation;
      }
    );

    watch(
      () => state.otbcFlow.locationRadius,
      (newRadius) => {
        locationRadius.value = newRadius;
      }
    );

    return {
      addressSuggestion,
      dealerRadius,
      dealerRadiusUnit,
      hiddenTitle,
      showSuggestions,
      textSuggestion,
      locationRadius,
      rangeControlRadius,
      getSuggestions,
      selectInitialLocation,
      selectLocationRadius,
      initialLocationText,
      handleOutsideInputClick,
      onBlurInput,
      onEnterPress,
      rangeControlActiveClass,
      rangeControlActiveTitleClass,
      locationInputErrorHandling,
      shouldShowRadiusGenericOption,
      buttonThemeClass,
      listGroupThemeClass,
      locationBoxInputIconClass,
      locationInputFocused,
      freeSearchReadyMode,
      locationInputClass,
      isScheduleRoute,
      shouldShowSuggestions,
      locationInputEnterPress,
      locationInput,
    };
  },
});
</script>

<style scoped lang="scss" src="./location-box.scss"></style>
